'use client';

import { forwardRef, useContext, useId, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { FieldSetContext } from '../field-set/context/field-set.context';
import { FormFieldContext } from '../form-field/context/form-field.context';
import { SwitchRootContext } from './context/switch-root.context';
import { switchRootRecipe } from './styles/switch-root.styles';
import type { SwitchRootPropsInterface } from './types/switch-root.types';

/**
 * @since 10.0.0
 */

export const SwitchRoot = forwardRef<HTMLDivElement, SwitchRootPropsInterface>(
  (props: SwitchRootPropsInterface, forwardedRef) => {
    const fieldSetContext = useContext(FieldSetContext);
    const formFieldContext = useContext(FormFieldContext);
    const defaultId = useId();

    const {
      checked,
      className,
      controlId = formFieldContext?.controlId ?? defaultId,
      css: cssProp,
      defaultChecked = checked ?? false,
      disabled = formFieldContext.disabled ?? false,
      size = fieldSetContext?.size ?? 'md',
      ...rest
    } = props;

    const styles = css(switchRootRecipe.raw({ size }), cssProp);

    const [checkedState, setCheckedState] = useControllableValue<boolean>({
      controlledValue: checked ?? false,
      uncontrolledValue: defaultChecked,
      isControlled: 'checked' in props,
      isUncontrolled: 'defaultChecked' in props,
      componentName: 'Switch.Root',
      controlledPropName: 'checked',
      uncontrolledPropName: 'defaultChecked',
    });

    const contextValue = useMemo(
      () => ({
        controlId,
        disabled,
        checkedState,
        setCheckedState,
        size,
      }),
      [controlId, disabled, checkedState, setCheckedState, size],
    );

    return (
      <SwitchRootContext.Provider value={contextValue}>
        <div
          className={cx('c11n', styles, className)}
          data-c11n-component="Switch.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          {...rest}
        />
      </SwitchRootContext.Provider>
    );
  },
);

SwitchRoot.displayName = 'Switch.Root';
